import { Component, Input, OnInit, inject } from '@angular/core';
import { PatientAttachment } from 'app/core/models/patient-attachment.model';
import { LimitOffsetPaging } from 'app/core/models/paged-results/limit-offset-paging.model';
import { PagedPatientAttachments, PatientAttachmentsService } from 'app/core/services/patient-attachments.service';
import { DownloadService } from 'app/core/services/download.service';
import { MatSlideToggleChange, MatSlideToggle } from '@angular/material/slide-toggle';
import { MeService } from '../../../core/services/me.service';
import { UserSettingsService } from '../../../core/services/user-settings.service';
import { UserSettings } from '../../../core/enums/user-settings.enum';
import { ClientConsultNoteFilters, ClientConsultNoteListSettings } from '../../../core/models/user-settings/client-consult-note-settings.model';
import { ClientsService } from '../../../core/services/clients.service';
import { AuthenticationService } from 'app/core/services/authentication.service';
import { debounce } from 'lodash-es';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { NgSelectComponent } from '@ng-select/ng-select';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BlankValueDashPipe } from '../../../shared/pipes/blank-value-dash.pipe';

@Component({
    selector: 'app-manually-uploaded-consult-notes',
    templateUrl: './manually-uploaded-consult-notes.component.html',
    imports: [FormsModule,
              NgSelectComponent,
              MatSlideToggle,
              PaginationComponent,
              MatProgressSpinner,
              DatePipe,
              BlankValueDashPipe]
})
export class ManuallyUploadedConsultNotesComponent implements OnInit {
  private patientAttachmentsService = inject(PatientAttachmentsService);
  private downloadService = inject(DownloadService);
  private meService = inject(MeService);
  private userSettingsService = inject(UserSettingsService);
  private clientsService = inject(ClientsService);
  private authService = inject(AuthenticationService);

  patientAttachments: PatientAttachment[] = [];
  paging: LimitOffsetPaging = LimitOffsetPaging.empty;
  loading = true;
  toggling: number[] = [];

  filters: ClientConsultNoteFilters = new ClientConsultNoteFilters();
  canFilterByClients = false;
  clients;

  @Input() disableEhrToggles: boolean;

  debounceFilterChange = debounce(
    () => {
      this.onFilterChange();
    },
    1000,
    { maxWait: 2000 }
  );

  ngOnInit() {
    this.canFilterByClients = this.authService.isCaptureAdminUser && !this.authService.viewingAsClient;
    this.loadQueueSettings();
    this.loadClients();
    this.loadPatientAttachments();
  }

  onPageSizeChange(size) {
    this.paging.pageSize = size;
    this.loadPatientAttachments();
  }

  onPageChange(page) {
    this.paging.currentPage = page;
    this.loadPatientAttachments();
  }

  onFilterChange() {
    this.saveQueueSettings();
    this.loadPatientAttachments();
  }

  onTextFilterChange() {
    this.debounceFilterChange();
  }

  onClearClick() {
    this.filters = new ClientConsultNoteFilters();
    this.saveQueueSettings();
    this.loadPatientAttachments();
  }

  private loadClients() {
    if (this.canFilterByClients) {
      this.clientsService.getList(null, { applicableToReferralClaims: true }).subscribe(result => {
        this.clients = result.clients;
      });
    }
  }

  disableEhrToggle(patientAttachmentId): boolean {
    return this.disableEhrToggles || this.toggling.includes(patientAttachmentId);
  }

  onUploadToggle({ source }: MatSlideToggleChange, patientAttachment: PatientAttachment) {
    this.toggling.push(patientAttachment.id);

    this.patientAttachmentsService
      .toggleEhrUpload(patientAttachment, source.checked)
      .subscribe((attachment: PatientAttachment) => {
        this.updateToggling(attachment);
        this.updateList(attachment);
        this.meService.notifyUserTodosChanged();
      });
  }

  private loadPatientAttachments() {
    this.loading = true;

    const { currentPage, pageSize } = this.paging;

    this.patientAttachmentsService
      .getManualUploadList(this.filters, currentPage, pageSize)
      .subscribe((pagedPatientAttachments: PagedPatientAttachments) => {
        this.paging = pagedPatientAttachments.meta.paging;
        this.patientAttachments = pagedPatientAttachments.patientAttachments || [];
        this.loading = false;
      });
  }

  download($event, patientAttachment: PatientAttachment) {
    $event.preventDefault();
    this.patientAttachmentsService.get(patientAttachment.id).subscribe(attachment => {
      this.downloadService.getDownloadUrl(attachment.fileExpirableDownloadPath).subscribe(expirableDownload => {
        this.downloadService.download(expirableDownload.url, null, '_blank');
      });
    });
  }

  updateList(patientAttachment: PatientAttachment) {
    const index = this.patientAttachments.findIndex(pa => pa.id === patientAttachment.id);

    if (index !== -1) {
      this.patientAttachments[index] = patientAttachment;
    }
  }

  updateToggling(patientAttachment: PatientAttachment) {
    const index = this.toggling.findIndex(id => id === patientAttachment.id);

    if (index !== -1) {
      this.toggling.splice(index, 1);
    }
  }

  get showTable(): boolean {
    return !this.loading && this.patientAttachments && this.patientAttachments.length > 0;
  }

  get noResults(): boolean {
    return !this.loading && this.patientAttachments.length === 0;
  }

  private loadQueueSettings() {
    const queueSettings: ClientConsultNoteListSettings = this.userSettingsService.get<ClientConsultNoteListSettings>(
      UserSettings.clientPortalConsultNoteListSettings
    );
    if (queueSettings) {
      this.filters = queueSettings.filters;
      this.paging = queueSettings.paging || LimitOffsetPaging.empty;

      // if we are viewing a client as a capture admin
      // and the capture admin has selectedClientIds set from
      // the admin view, remove those selected client ids.
      if (!this.canFilterByClients) {
        this.filters.selectedClientIds = [];
      }
    }
  }

  private saveQueueSettings() {
    const queueSettings = new ClientConsultNoteListSettings();
    queueSettings.filters = this.filters;
    queueSettings.paging = this.paging;
    this.userSettingsService.save<ClientConsultNoteListSettings>(
      UserSettings.clientPortalConsultNoteListSettings, queueSettings
    );
  }
}
